import { Link, Outlet } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth';
import { APP_VERSION } from '@/lib/version';

/**
 * App chrome. Two layouts:
 *   ≥ 900 px  →  Sidebar (left) + main pane
 *   < 900 px  →  Topbar + main pane + fixed BottomNav
 *
 * Matches Design/app/shell.jsx prototype (Option A: editorial console).
 */

interface NavItem {
  to: string;
  label: string;
  short: string;
  icon: JSX.Element;
}

const NAV: ReadonlyArray<NavItem> = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    short: 'Home',
    icon: <path d="M3 12L12 4l9 8M5 10v10h14V10" />,
  },
  {
    to: '/costs',
    label: 'Costs',
    short: 'Costs',
    // Pound coin — circle with a £ glyph inside.
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 16.5h6M9.5 12.5h3.5M14 16.5c-2 0-3.5-1-3.5-3V10c0-1.7 1.2-3 3-3 1.3 0 2.3.8 2.5 2" />
      </>
    ),
  },
  {
    to: '/automations',
    label: 'Automations',
    short: 'Auto',
    // Connected workflow nodes — three circles wired together.
    icon: (
      <>
        <circle cx="5" cy="6" r="2" />
        <circle cx="19" cy="12" r="2" />
        <circle cx="5" cy="18" r="2" />
        <path d="M7 6h7a3 3 0 013 3v1M7 18h7a3 3 0 003-3v-1" />
      </>
    ),
  },
  {
    to: '/integrations',
    label: 'Integrations',
    short: 'Ints',
    // Power plug — connects systems together.
    icon: (
      <>
        <path d="M9 3v4M15 3v4" />
        <path d="M6 7h12v5a6 6 0 01-12 0V7z" />
        <path d="M12 18v3" />
      </>
    ),
  },
  {
    to: '/alerts',
    label: 'Alerts',
    short: 'Alerts',
    icon: (
      <>
        <path d="M12 3l9 16H3z" />
        <path d="M12 10v4M12 17h.01" />
      </>
    ),
  },
  {
    to: '/audit-log',
    label: 'Audit Log',
    short: 'Audit',
    // Document with a checkmark — an audited record.
    icon: (
      <>
        <path d="M14 3H6a1 1 0 00-1 1v16a1 1 0 001 1h12a1 1 0 001-1V8z" />
        <path d="M14 3v5h5" />
        <path d="M9 14l2 2 4-4" />
      </>
    ),
  },
  {
    to: '/web-apps',
    label: 'Web Apps',
    short: 'Apps',
    // Browser window with a 2×2 grid of tiles.
    icon: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="1" />
        <path d="M3 8h18" />
        <path d="M8 12h3v4H8zM13 12h3v4h-3z" />
      </>
    ),
  },
  {
    to: '/reports',
    label: 'Reports',
    short: 'Reports',
    // Bar chart — operational reporting.
    icon: (
      <>
        <path d="M4 20V4" />
        <path d="M4 20h16" />
        <rect x="7" y="12" width="3" height="5" />
        <rect x="12" y="8" width="3" height="9" />
        <rect x="17" y="14" width="3" height="3" />
      </>
    ),
  },
  {
    to: '/archive',
    label: 'Archive',
    short: 'Arch.',
    icon: (
      <>
        <rect x="3" y="5" width="18" height="14" />
        <path d="M3 9h18" />
      </>
    ),
  },
  {
    to: '/admin',
    label: 'Admin',
    short: 'Admin',
    // Shield with keyhole — admin / privileged settings.
    icon: (
      <>
        <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />
        <circle cx="12" cy="11" r="1.5" />
        <path d="M12 12.5v3" />
      </>
    ),
  },
];

// Bottom nav (mobile) hides Admin and the not-yet-built placeholder
// pages behind the desktop layout — saves space, keeps the 6-col grid,
// and these rarely matter on a phone.
const MOBILE_HIDDEN = new Set(['/admin', '/audit-log', '/web-apps', '/reports']);
const BOTTOM_NAV = NAV.filter((n) => !MOBILE_HIDDEN.has(n.to));

function NavIcon({ children, className }: { children: JSX.Element; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className ?? 'w-[18px] h-[18px]'}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function IdentityBlock() {
  const { me, isLoading, isUnauthorized } = useAuth();
  if (isLoading) {
    return <div className="text-caption text-fg-muted">…</div>;
  }
  if (isUnauthorized) {
    return <div className="text-caption text-sev-crit mono">Signed out</div>;
  }
  if (!me) return null;
  const initials = (me.name || me.email)
    .split(/[\s.@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join('');
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-8 h-8 rounded-full bg-fg-default text-mb-spring-wood flex items-center justify-center text-caption font-bold tracking-[0.06em] flex-shrink-0">
        {initials}
      </div>
      <div className="leading-tight min-w-0 flex-1">
        <div className="text-small truncate" title={me.email}>{me.name || me.email}</div>
        <div className="text-caption text-fg-muted uppercase tracking-eyebrow font-bold">
          {me.role}
        </div>
      </div>
    </div>
  );
}

function Sidebar() {
  const { me } = useAuth();
  const env = me?.env ?? 'staging';
  return (
    <aside className="hidden lg:flex flex-col w-[240px] bg-bg-panel border-r border-rule sticky top-0 h-screen px-4 py-6">
      <Link to="/dashboard" className="border-b-0 pb-4 border-b border-rule mb-4">
        <div className="font-light tracking-heading uppercase text-h3 text-fg-default">
          Millboard
        </div>
        <div className="eyebrow mt-1">IT Ops Hub</div>
      </Link>
      <nav className="flex flex-col gap-px">
        {NAV.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="border-b-0 flex items-center gap-3 px-3 py-2.5 text-small text-fg-default border-l-2 border-transparent hover:bg-bg-page transition-colors"
            activeProps={{
              className: 'bg-bg-page font-bold border-l-mb-olive',
            }}
          >
            <NavIcon>{item.icon}</NavIcon>
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="mt-6 pt-4 border-t border-rule">
        <span className="eyebrow">Environment</span>
        <div className="text-small mt-1 uppercase tracking-[0.08em]">{env}</div>
        <div className="text-caption text-fg-muted mt-0.5 mono">
          {env === 'production'
            ? 'mb-it-ops.millboard-it.com'
            : 'mb-it-ops-staging.millboard-it.com'}
        </div>
      </div>
      <div className="mt-auto pt-4 border-t border-rule">
        <IdentityBlock />
      </div>
      <div className="text-caption text-fg-muted mt-3 mono">{APP_VERSION}</div>
    </aside>
  );
}

function Topbar() {
  return (
    <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-bg-page border-b border-rule sticky top-0 z-20">
      <div>
        <div className="font-light tracking-heading uppercase text-body text-fg-default">
          Millboard
        </div>
        <div className="eyebrow text-fg-muted mt-0.5">IT Ops Hub</div>
      </div>
      <IdentityBlock />
    </header>
  );
}

function BottomNav() {
  return (
    <nav
      className="lg:hidden fixed left-0 right-0 bottom-0 grid grid-cols-6 bg-bg-page border-t border-rule px-1 pt-2 pb-3 z-30"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 12px), 12px)' }}
    >
      {BOTTOM_NAV.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="border-b-0 flex flex-col items-center gap-1 py-1.5 text-[9px] tracking-[0.10em] uppercase font-bold text-fg-muted relative"
          activeProps={{
            className: 'text-fg-default after:absolute after:top-0 after:w-6 after:h-0.5 after:bg-mb-olive',
          }}
        >
          <NavIcon>{item.icon}</NavIcon>
          <span>{item.short}</span>
        </Link>
      ))}
    </nav>
  );
}

export function RootLayout() {
  return (
    <div className="min-h-screen bg-bg-page text-fg-default lg:grid lg:grid-cols-[240px_1fr]">
      <Sidebar />
      <div className="min-w-0">
        <Topbar />
        <main className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 pb-24 lg:pb-8">
          <Outlet />
        </main>
      </div>
      <BottomNav />
    </div>
  );
}
